import { useState, useEffect } from 'react';
import { Copy, Download, Search, FileText } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { copyToClipboard, download, readFileAsArrayBuffer } from '@/lib/utils';
import { toast } from 'sonner';
import { pdfjs } from '@/lib/pdfjs';
import { cn } from '@/lib/cn';

interface PageText {
  page: number;
  text: string;
}

export function PdfTextExtract() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState<PageText[]>([]);
  const [working, setWorking] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setPages([]);
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(f);
      const doc = await pdfjs.getDocument({ data: buf }).promise;
      const out: PageText[] = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const content = await page.getTextContent();
        const text = content.items
          .map((it) => ('str' in it ? it.str : ''))
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();
        out.push({ page: i, text });
      }
      setPages(out);
    } catch {
      toast.error('Could not extract text — PDF may be scanned (try OCR)');
    } finally {
      setWorking(false);
    }
  }

  const fullText = pages.map((p) => `--- Page ${p.page} ---\n${p.text}`).join('\n\n');
  const totalChars = pages.reduce((s, p) => s + p.text.length, 0);
  const totalWords = pages.reduce((s, p) => s + p.text.split(/\s+/).filter(Boolean).length, 0);

  const filtered = search.trim()
    ? pages.filter((p) => p.text.toLowerCase().includes(search.toLowerCase()))
    : pages;

  function highlight(text: string): string {
    if (!search.trim()) return escape(text);
    const re = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return escape(text).replace(re, '<mark class="bg-warning/40 rounded px-0.5">$1</mark>');
  }

  return (
    <div>
      <ToolHeader
        toolId="pdf-text-extract"
        title="PDF text extract & search"
        description="Pull all text out of a PDF, search across pages, copy or save."
        badges={
          pages.length ? (
            <>
              <Badge>{pages.length} pages</Badge>
              <Badge>{totalWords.toLocaleString()} words</Badge>
            </>
          ) : null
        }
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={handleFile} icon={<FileText size={20} />} />
      ) : working ? (
        <Card>
          <div className="py-8 text-center text-sm text-fg-subtle">Extracting text…</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          <div className="space-y-3">
            <Card>
              <div className="text-sm font-medium truncate mb-2">{file.name}</div>
              <div className="space-y-1.5 text-xs text-fg-muted">
                <Row label="Pages" value={pages.length.toString()} />
                <Row label="Words" value={totalWords.toLocaleString()} />
                <Row label="Characters" value={totalChars.toLocaleString()} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-border">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    const ok = await copyToClipboard(fullText);
                    if (ok) toast.success('Copied');
                  }}
                >
                  <Copy size={11} />
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => download(fullText, file.name.replace(/\.pdf$/i, '') + '.txt', 'text/plain')}
                >
                  <Download size={11} />
                  TXT
                </Button>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="w-full mt-2"
                onClick={() => { setFile(null); setPages([]); }}
              >
                Change
              </Button>
            </Card>
          </div>
          <Card className="!p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border">
              <Input
                icon={<Search size={12} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search across pages…"
                className="!h-8"
              />
            </div>
            <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="p-6 text-sm text-fg-subtle text-center">
                  {search ? `No pages contain "${search}"` : 'No text extracted'}
                </div>
              ) : (
                filtered.map((p) => (
                  <div key={p.page} className="p-4">
                    <div className={cn('text-2xs font-mono uppercase tracking-wider text-fg-subtle mb-2')}>
                      Page {p.page}
                    </div>
                    <p
                      className="text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: highlight(p.text) || '<span class="text-fg-subtle italic">empty</span>' }}
                    />
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span>{label}</span>
      <span className="font-mono text-fg">{value}</span>
    </div>
  );
}

export default PdfTextExtract;
